import animation, { fadeInLeft, slideInUp } from '@helpers/animation';
import brand from '@helpers/brand';
import fonts from '@helpers/fonts';
import global from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { CardSliderStyles } from '@stories/Components/Media/CardSlider/CardSlider.styles';
import { PerfectMatchCardStyles } from '@stories/Elements/Cards/PerfectMatchCard/PerfectMatchCard.styles';
import { ProductCardStyles } from '@stories/Elements/Cards/ProductCard/ProductCard.styles';
import { SliderButtonStyles } from '@stories/Elements/Media/SliderButton/SliderButton.styles';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import styled, { css } from 'styled-components';

const Tabs = styled.div`
  display: flex;
  flex-direction: row;
  gap: 25px;
  margin: 35px 0px 25px 0px;
  overflow: auto;

  @media ${from(Device.TabletLarge)} {
    margin: 35px 0px 48px 0px;
    gap: 40px;
  }
`;

const Tab = styled.span<{ isActive: boolean }>`
  ${fonts.helvetica['87-heavy-condensed']};
  font-size: 22px;
  padding-bottom: 6px;
  text-transform: uppercase;
  cursor: pointer;
  position: relative;
  user-select: none;
  transition: color 250ms ${animation.easeInOutCubic};
  color: ${brand.black};
  flex: 0 0 auto;

  [data-theme='perfect-match'] & {
    color: ${brand.white};
  }

  ${({ isActive }) =>
    isActive
      ? css`
          color: ${brand.red.base};

          [data-theme='perfect-match'] & {
            color: ${brand.white};
          }

          &:after {
            content: '';
            height: 3px;
            width: 100%;
            position: absolute;
            bottom: 0px;
            left: 0px;
            transition: background 250ms ${animation.easeInOutCubic};
            background-color: ${brand.red.base};

            [data-theme='perfect-match'] & {
              background-color: ${brand.white};
            }
          }
        `
      : null}
`;

const Outer = styled.div``;

const Wrapper = styled.section`
  padding: 38px 0;
  transition: background 250ms ${animation.easeInOutCubic};
  background: ${brand.grey[97]};

  &[data-theme='perfect-match'] {
    background: ${brand.red.base};
  }

  @media ${from(Device.Tablet)} {
    padding: 54px 0;
  }

  @media ${from(Device.TabletLarge)} {
    padding: 74px 0;
  }

  .slick-list {
    overflow: visible !important; // force visible slides to the right of container
  }
`;

const Inner = styled.div`
  grid-column: span 12;
  overflow: hidden;
`;

const Title = styled.h2<{ inView: boolean }>`
  ${Typography.H2Style};
  ${fonts.helvetica['97-black-condensed']};
  padding-top: 6px;
  margin-bottom: 35px;
  padding-right: ${global.contentPadding}px;
  color: ${brand.black};
  transition: color 250ms ${animation.easeInOutCubic};

  ${({ inView }) => (inView !== undefined ? slideInUp(inView, 575, 0, animation.easeInOutCubic) : null)};

  [data-theme='perfect-match'] & {
    color: ${brand.white};
  }
`;

const SliderWrapper = styled.div`
  @media ${until(Device.ActualDesktop)} {
    padding-right: 64px;

    @media ${from(Device.Tablet)} {
      padding-right: 86px;
    }
  }

  ${ProductCardStyles.Card} {
    margin-right: 16px;
    height: 100%;

    @media ${from(Device.TabletLarge)} {
      margin-right: 30px;
    }
  }

  ${CardSliderStyles.SliderControls} {
    position: unset;
  }

  ${SliderButtonStyles.SliderButtonElement} {
    display: inline-block;
    position: unset;
    margin-left: 0px;
    margin-right: 10px;

    @media ${from(Device.Tablet)} {
      margin-top: 36px;
    }
  }
`;

const SlidesGrid = styled.div<{ slidesToShow: number }>`
  display: grid;
  grid-template-columns: repeat(12, 1fr);

  ${({ slidesToShow }) => css`
    ${ProductCardStyles.Card} {
      grid-column: span ${12 / slidesToShow};
      margin-right: 28px;
    }
  `}
`;

const FilterWrapper = styled.div<{ inView: boolean }>`
  padding-right: ${global.contentPadding}px;
  opacity: 0;
  transform: translateX(-25px);
  z-index: 10;
  ${({ inView }) => (inView ? fadeInLeft(inView, 25, 575, 425, animation.easeInOutCubic) : null)};

  @media ${from(Device.TabletLarge)} {
    position: absolute;
    top: 5px;
    right: 30px;
    width: 100%;
    max-width: 375px;
    padding-right: unset;
  }
`;

const ButtonWrapper = styled.div<{ inView: boolean }>`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  ${({ inView }) => (inView ? fadeInLeft(inView, 25, 575, 625, animation.easeInOutCubic) : null)};
`;

const PerfectMatchGrid = styled(Grid.DefaultInner)`
  @media ${from(Device.TabletLarge)} {
    grid-template-rows: repeat(3, auto);
    row-gap: 0;
  }

  ${ProductCardStyles.Card} {
    grid-column: span 12;

    @media ${from(Device.Mobile)} {
      grid-column: span 6;
    }

    @media ${from(Device.TabletLarge)} {
      grid-column: span 3;
      grid-row: 2;
    }
  }

  ${PerfectMatchCardStyles.Card} {
    grid-column: span 12;

    @media ${from(Device.TabletLarge)} {
      grid-column: span 5;
      grid-row: 1 / -1;
    }

    @media ${from(Device.Desktop)} {
      grid-column: span 4 / -1;
    }
  }
`;

const PerfectMatchPlus = styled.span`
  grid-column: span 1;
  display: none;
  width: 45px;
  height: 45px;
  position: relative;
  background-color: ${brand.black};
  align-self: center;
  justify-self: center;

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 22px;
    height: 3px;
    transform: translate(-50%, -50%);
    background-color: ${brand.white};

    @media ${from(Device.Desktop)} {
      width: 33px;
      height: 4px;
    }
  }

  &:after {
    transform: translate(-50%, -50%) rotate(90deg);
  }

  @media ${from(Device.TabletLarge)} {
    display: block;
    grid-row: 2;
  }

  @media ${from(Device.Desktop)} {
    width: 65px;
    height: 65px;
  }
`;

export const ProductSuggestionsStyles = {
  Title,
  Tabs,
  Tab,
  Wrapper,
  Outer,
  Inner,
  SliderWrapper,
  SlidesGrid,
  FilterWrapper,
  ButtonWrapper,
  PerfectMatchGrid,
  PerfectMatchPlus,
};
